import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const inputCls = "w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1A365D] focus:border-transparent transition-all outline-none";

const fmtD = (d) => d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });

export default function InquiryForm({ propertyId = "", propertyName = "", range = null }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: propertyName ? `I'd love to know more about ${propertyName}. ` : "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // When the guest picks dates on the calendar, auto-fill the message.
  useEffect(() => {
    if (range?.from && range?.to) {
      const nights = Math.round((range.to - range.from) / 86400000);
      const base = propertyName ? `I'd like to request ${propertyName} ` : "I'd like to request these dates ";
      setForm((f) => ({ ...f, message: `${base}from ${fmtD(range.from)} to ${fmtD(range.to)} (${nights} night${nights !== 1 ? "s" : ""}). Please confirm availability and pricing.` }));
    }
  }, [range?.from, range?.to, propertyName]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", { ...form, property_id: propertyId });
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Inquiry sent! We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="inquiry-success" className="bg-[#81B29A]/10 border border-[#81B29A]/40 rounded-2xl p-8 text-center">
        <p className="font-display text-3xl text-[#1A365D] mb-2">Thank you!</p>
        <p className="text-gray-600">Your inquiry has reached our concierge team. We'll reply within 24 hours.</p>
        <button onClick={() => setSent(false)} className="mt-5 text-sm font-medium text-[#1A365D] hover:underline">Send another message</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="inquiry-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input data-testid="inquiry-name" required className={inputCls} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input data-testid="inquiry-email" type="email" required className={inputCls} placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </div>
      <input data-testid="inquiry-phone" className={inputCls} placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      <textarea data-testid="inquiry-message" required rows="4" className={inputCls} placeholder="Tell us your dates, group size and what you're dreaming of…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
      <button data-testid="inquiry-submit" type="submit" disabled={sending} className="w-full sm:w-auto bg-[#E07A5F] text-white hover:bg-[#D0654B] rounded-full px-8 py-3 font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
        {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} Send Inquiry
      </button>
    </form>
  );
}
